import axiosRetry from 'axios-retry';
import axios from 'axios';

const DEFAULT_LANG = "en";
const DEFAULT_AUTH_TOKEN_NAME = "token";
const REQUIRED_HEADERS = ["version", "x-api-key", "language"];
const REQUIRED_INSTALLATION_PROPERTIES = ["apiUrl", "apiKey", "onErrorHandler"];
const ONE_SECOND = 1e3;
const AUTH_COOKIE_TTL_DAYS = 60;

const installationValidator = (props) => {
  REQUIRED_INSTALLATION_PROPERTIES.forEach((propsName) => {
    if (!props[propsName])
      throw new Error(`[API CLIENT]: "${propsName}" property is required for installation`);
  });
};

const CODES_FOR_RETRY = ["ECONNABORTED", "ERR_NETWORK"];
const retryVerifier = (error) => {
  if (!error.code)
    return false;
  return CODES_FOR_RETRY.includes(error.code);
};

const retryHandler = (callback) => (retryCount, error, requestConfig) => {
  if (callback)
    callback(retryCount, error, requestConfig);
};

const countRetryDelay = (retryCount) => retryCount * ONE_SECOND;

const getRequest = (instance) => async (url, params, options) => {
  const { timeout, headers = {} } = options || {};
  return instance.get(url, {
    params,
    timeout,
    headers: {
      ...instance.defaults.headers.common,
      ...headers
    }
  });
};

const postRequest = (instance) => async (url, data, options) => {
  const { timeout, headers = {} } = options || {};
  return instance.post(url, data, {
    timeout,
    headers: {
      ...instance.defaults.headers.common,
      ...headers
    }
  });
};

const putRequest = (instance) => async (url, data, options) => {
  const { timeout, headers = {} } = options || {};
  return instance.put(url, data, {
    timeout,
    headers: {
      ...instance.defaults.headers.common,
      ...headers
    }
  });
};

const patchRequest = (instance) => async (url, data, options) => {
  const { timeout, headers = {} } = options || {};
  return instance.patch(url, data, {
    timeout,
    headers: {
      ...instance.defaults.headers.common,
      ...headers
    }
  });
};

const deleteRequest = (instance) => async (url, data, options) => {
  const { timeout, headers = {} } = options || {};
  return instance.delete(url, {
    data,
    timeout,
    headers: {
      ...instance.defaults.headers.common,
      ...headers
    }
  });
};

const HEADER_MISSING = "Required header is missing";

const getLanguage = () => {
  const language = window?.localStorage?.getItem("language");
  if (!language) {
    return DEFAULT_LANG;
  }
  return language;
};

const MILLISECONDS_PER_DAY = 864e5;
const EXPIRES_DATE_FOR_DELETE = "; expires=Thu, 01 Jan 1970 00:00:01 GMT";
const setCookie = (name, value, days) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * MILLISECONDS_PER_DAY);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ""}${expires}; path=/; SameSite=Strict;`;
};
const deleteCookie = (name, path, domain) => {
  if (getCookie(name)) {
    const pathParam = path ? `; path=${path}` : "";
    const domainParam = domain ? `; domain=${domain}` : "";
    document.cookie = `${name}=${pathParam}${domainParam}${EXPIRES_DATE_FOR_DELETE}`;
  }
};
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2)
    return parts.pop()?.split(";").shift();
};

const onFulfilledRequest = ({
  errorHandler,
  requestInterceptors,
  authTokenKey,
  cookieAuth,
  getAuthToken
}) => {
  return (config) => {
    const language = getLanguage();
    let token = window?.localStorage?.getItem?.(String(authTokenKey));
    if (cookieAuth) {
      token = getCookie(String(authTokenKey)) ?? token;
    }
    if (getAuthToken) {
      token = getAuthToken();
    }
    if (config.headers) {
      if (token)
        config.headers.token = token;
      if (language)
        config.headers.language = language;
      config.headers.screenId = window.location.pathname.replace("/", "");
      const missingHeader = REQUIRED_HEADERS.some(
        (header) => !Boolean(config?.headers?.[header])
      );
      if (missingHeader) {
        const headersMissingErrors = new Error(`${HEADER_MISSING}: ${missingHeader}`);
        errorHandler(headersMissingErrors, {
          ...config
        });
        throw headersMissingErrors;
      }
    }
    let requestConfig = config;
    if (requestInterceptors) {
      requestConfig = requestInterceptors.reduce((config2, functor) => {
        return functor(config2);
      }, config);
    }
    return requestConfig;
  };
};

const onFulfilledResponse = ({
  onUpdateToken,
  extendResponse,
  cookieAuth,
  authTokenKey,
  tokenTTL = AUTH_COOKIE_TTL_DAYS
}) => {
  return (response) => {
    if (response.headers.token) {
      onUpdateToken && onUpdateToken(response.headers.token);
      if (cookieAuth) {
        setCookie(authTokenKey, response.headers.token, tokenTTL);
      }
      response.data.token = response.headers.token;
    }
    if (response.headers.country)
      response.data.country = response.headers.country;
    return extendResponse ? response : response.data;
  };
};

const onRejectedResponse = ({ errorHandler, onAuthorisationError, authTokenKey, cookieAuth }) => (error) => {
  if (onAuthorisationError) {
    const status = error?.response?.status;
    if (status === 403 || status === 401) {
      if (cookieAuth) {
        deleteCookie(authTokenKey);
      }
      onAuthorisationError(error);
    }
  }
  errorHandler(error);
  return Promise.reject(error);
};

const onRejectedRequest = ({ errorHandler }) => {
  return (error) => {
    errorHandler(error);
    return Promise.reject(error);
  };
};

const httpErrors = {
  400: "BAD REQUEST",
  401: "UNAUTHORIZED",
  402: "PAYMENT REQUIRED",
  403: "FORBIDDEN",
  404: "NOT FOUND",
  405: "METHOD NOT ALLOWED",
  406: "NOT ACCEPTABLE",
  407: "PROXY AUTHENTICATION REQUIRED",
  408: "REQUEST TIMEOUT",
  409: "CONFLICT",
  410: "GONE",
  411: "LENGTH REQUIRED",
  412: "PRECONDITION FAILED",
  413: "REQUEST ENTITY TOO LARGE",
  414: "REQUEST-URI TOO LONG",
  415: "UNSUPPORTED MEDIA TYPE",
  416: "REQUESTED RANGE NOT SATISFIABLE",
  417: "EXPECTATION FAILED",
  421: "MISDIRECTED REQUEST",
  422: "UNPROCESSABLE ENTITY",
  423: "LOCKED",
  424: "FAILED DEPENDENCY",
  425: "TOO EARLY",
  426: "UPGRADE REQUIRED",
  428: "PRECONDITION REQUIRED",
  429: "TOO MANY REQUESTS",
  431: "REQUEST HEADER FIELDS TOO LARGE",
  451: "UNAVAILABLE FOR LEGAL REASONS",
  500: "INTERNAL SERVER ERROR",
  501: "NOT IMPLEMENTED",
  502: "BAD GATEWAY",
  503: "SERVICE UNAVAILABLE",
  504: "GATEWAY TIMEOUT",
  505: "HTTP VERSION NOT SUPPORTED",
  506: "VARIANT ALSO NEGOTIATES",
  507: "INSUFFICIENT STORAGE",
  508: "LOOP DETECTED",
  510: "NOT EXTENDED",
  511: "NETWORK AUTHENTICATION REQUIRED"
};

const getErrorHandler = (onErrorHandler) => {
  return (error, config) => {
    const response = error?.response;
    const request = error?.request;
    const status = response?.status;
    const codeErrorMessage = httpErrors[String(status)];
    const message = codeErrorMessage ? `${codeErrorMessage} - ${status}` : error.message;
    const extras = {
      requestURL: request?.responseURL,
      response,
      status,
      message
    };
    onErrorHandler({ error, config, extras, message, status });
  };
};

const isApiClientError = (error) => {
  return axios.isAxiosError(error);
};

const addErrorListener = (instance) => (handleError) => {
  const errorInterceptor = instance.interceptors.response.use(null, (error) => {
    handleError(error);
    return Promise.reject(error);
  });
  return () => {
    instance.interceptors.response.eject(errorInterceptor);
  };
};

const initApiClient = (props) => {
  installationValidator(props);
  const {
    apiUrl: baseURL,
    apiVersion,
    apiKey,
    requestInterceptors,
    extendResponse = false,
    retries = 0,
    authTokenKey = DEFAULT_AUTH_TOKEN_NAME,
    onUpdateToken,
    onRetry,
    onErrorHandler,
    onAuthorisationError,
    getAuthToken,
    retriesDelay,
    timeout = 0,
    cookieAuth = false,
    tokenTTL
  } = props;
  const errorHandler = getErrorHandler(onErrorHandler);
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      version: apiVersion,
      timeout,
      language: DEFAULT_LANG
    }
  });
  instance.interceptors.request.use(
    onFulfilledRequest({ errorHandler, requestInterceptors, authTokenKey, cookieAuth, getAuthToken }),
    onRejectedRequest({ errorHandler })
  );
  instance.interceptors.response.use(
    onFulfilledResponse({
      onUpdateToken,
      extendResponse,
      authTokenKey,
      cookieAuth,
      tokenTTL
    }),
    onRejectedResponse({
      errorHandler,
      onAuthorisationError,
      authTokenKey,
      cookieAuth
    })
  );
  if (retries) {
    axiosRetry(instance, {
      retries,
      onRetry: retryHandler(onRetry),
      retryDelay: !retriesDelay ? countRetryDelay : () => retriesDelay,
      retryCondition: retryVerifier
    });
  }
  return {
    __instance__: instance,
    get: getRequest(instance),
    post: postRequest(instance),
    put: putRequest(instance),
    patch: patchRequest(instance),
    delete: deleteRequest(instance),
    onError: addErrorListener(instance),
    utils: {
      cookies: {
        setToken: (token) => setCookie(authTokenKey, token, tokenTTL),
        getToken: () => getCookie(authTokenKey),
        deleteToken: () => deleteCookie(authTokenKey)
      }
    }
  };
};

export { initApiClient, isApiClientError };
//# sourceMappingURL=bundle.js.map
